import { ActivityEntityName, EntityViewDisplay, SurfaceType, TaskSecurityRight, TaskTemplate } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import type { EntityBoardListConfig, EntityBoardReorderConfig } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { resolveTaskTemplateEvidenceListProperty, resolveTaskTemplateTitleAdornment } from './task.template.list.presentation';

// Task Template entity manifest: parent-scoped list presentation, Form create, and Detail edit/preview.

/** Same policy for the staging board and the embedded template tabs — one row is one template. */
const taskTemplateList: EntityBoardListConfig = {
  displayProperties: {
    allow: ['target', 'type', 'evidence', 'phase', 'tags', 'createdAt', 'updatedAt'],
  },
  fixedProperties: ['evidence'],
  propertyPresentations: { evidence: resolveTaskTemplateEvidenceListProperty },
  titleAdornment: resolveTaskTemplateTitleAdornment,
};

/**
 * Lex reorder within the parent. `sortOrder` is assigned server-side from the last rank in
 * `parent._id`, so within-group is the only ordering that means anything.
 */
const taskTemplateReorder: EntityBoardReorderConfig<TaskTemplate> = {
  field: 'sortOrder',
  displays: [EntityViewDisplay.List],
  withinGroupOnly: true,
  canReorder: ({ context, item }) => context.readonly !== true && item?.virtual !== true,
};

/**
 * Task Template board — list-first staging under finance-pro/task-templates (parent-scoped checklist).
 *
 * Templates are always parent-scoped: a checklist, a work-compliance list, a job template, a stage or
 * a recurring job. The data strategy returns nothing without `parentId`; there is no top-level board.
 * No `routes.record` — a template has no page of its own, only the board's quickview panel.
 */
export const taskTemplateManifest = registerManifest<TaskTemplate>({
  entity: ActivityEntityName.TaskTemplate,
  slug: 'task-template',

  surfaces: {
    [SurfaceType.Form]: () => import('./surface/create/task.template.create.surface').then(m => m.taskTemplateCreateFormSurface),
    [SurfaceType.Detail]: () => import('./surface/detail/task.template.detail.surface').then(m => m.taskTemplateDetailSurface),
  },
  operations: {
    create: {
      surface: SurfaceType.Form,
      presentation: {
        mode: 'dialog',
        width: 'min(728px, calc(100vw - 40px))',
      },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(TaskSecurityRight.ManageTaskChecklists),
    },
    edit: {
      surface: SurfaceType.Detail,
    },
    preview: {
      surface: SurfaceType.Detail,
    },
  },

  board: {
    supportedDisplays: [EntityViewDisplay.List],
    defaults: {
      panel: {
        kind: 'quickview',
        width: 440,
        minWidth: 360,
        maxWidth: 520,
      },
      header: {
        title: 'Task Templates',
        showTitle: true,
        showSearch: true,
        showPaginator: false,
        showViewOptions: false,
        defaultViewId: `${ActivityEntityName.TaskTemplate}-embedded-list`,
      },
      list: taskTemplateList,
      reorder: taskTemplateReorder,
    },
    embedded: {
      list: taskTemplateList,
      reorder: taskTemplateReorder,
    },
  },
});
